import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
    ArrowUpDown,
    ChevronDown,
    ExternalLink,
    InfoIcon,
    MessageCircleWarning,
    Pencil,
} from 'lucide-react';
import { useState, Suspense } from 'react';
import Load from '~/components/Load';
import { PartnerDialog } from '~/partials/PartnerDialog';
import TooltipButton from '~/partials/Tooltip';
import {
    levelCreatorsQuery,
    levelPacksQuery,
} from '~/util/queries/public/levels';
import { ScrollArea } from '~/primitives/scroll-area';
import { LevelGPTags, LevelPacksTags, UserTags } from '~/partials/Tags';
import History from './-LevelHistory';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '~/primitives/collapsible';
import { cn, trimStringToNull } from '~/util';
import { Checkbox } from '~/primitives/checkbox';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useList } from '~/util/hooks/useList';
import { toast } from 'react-toastify';
import { idlQuery } from '~/util/queries/public/other';
import { useScopes } from '~/components/Providers/AuthProvider';
import { EditLevelSheet } from './-EditLevelSheet';
import { MoveLevelSheet } from './-MoveLevelSheet';
import { VideoPlayer } from '~/components/VideoPlayer';
import { LevelCustomCopies } from './-LevelCustomCopies';
import { sortTags } from '~/util/types/tags';
import { LevelNotes } from './-LevelNotes';
import { LevelUpdates } from './-LevelUpdates';

const Route = getRouteApi('/list/$levelid');

interface LevelData {
    label: string;
    data: string;
    link?: string;
    info?: string;
    warn?: string;
}

function LevelCreators() {
    const { levelid } = Route.useParams();
    const list = useList();

    const baseData = Route.useLoaderData();
    const { data: creatorsData } = useSuspenseQuery(
        levelCreatorsQuery({ list, levelid }),
    );

    return (
        <ScrollArea className={`h-[${creatorsData.length > 0 ? 20 : 10}vh]`}>
            <div className="grid mt-2 mr-4 grid-cols-[max-content_3fr] grid-rows-auto gap-4 text-justify">
                <span className="uppercase text-primary font-light">
                    {creatorsData?.length > 0
                        ? 'Publisher'
                        : 'Publisher \n& Creator'}
                </span>
                <UserTags item={baseData?.publisher} />
                <span className="uppercase text-primary font-light">
                    Verifier
                    {baseData?.verifications.length > 1 ? 's' : ''}
                </span>
                <UserTags
                    items={baseData?.verifications.map(
                        (verification) => verification.submitted_by,
                    )}
                />
                {creatorsData?.length > 0 && (
                    <>
                        <span className="uppercase text-primary font-light">
                            {creatorsData?.length == 1 ? 'Creator' : 'Creators'}
                        </span>
                        <UserTags items={creatorsData} />
                    </>
                )}
            </div>
        </ScrollArea>
    );
}

function LevelDataInfo({ label, data, link, info, warn }: LevelData) {
    const [isMainModalOpen, setMainModalOpen] = useState(false);
    const [isWarnModalOpen, setWarnModalOpen] = useState(false);

    return (
        <div className="flex-1 text-center uppercase p-1 border-r border-secondary-light last:border-none">
            <span className="flex justify-center items-center gap-1 text-primary clamp-[text-xs-lg-clamp] whitespace-nowrap">
                {label}{' '}
                {info && (
                    <TooltipButton text={`What is ${info}?`} side="top">
                        <InfoIcon
                            size={15}
                            onClick={() => setMainModalOpen(true)}
                            className="cursor-pointer"
                        />
                    </TooltipButton>
                )}
            </span>
            <span className="flex justify-center items-center text-secondary-foreground clamp-[text-xl-3xl-clamp]">
                {link ? (
                    <Link
                        target="_blank"
                        to={link}
                        className="flex items-center"
                    >
                        {data}
                        <ExternalLink className="ml-1 clamp-[text-xs-sm-clamp]" />
                    </Link>
                ) : (
                    data
                )}
                {warn && (
                    <MessageCircleWarning
                        onClick={() => setWarnModalOpen(true)}
                        className="ml-2 cursor-pointer text-warning"
                    />
                )}
            </span>
            {info && (
                <PartnerDialog
                    isOpen={isMainModalOpen}
                    onClose={() => setMainModalOpen(false)}
                    infoType={info}
                />
            )}
            {warn && (
                <PartnerDialog
                    isOpen={isWarnModalOpen}
                    onClose={() => setWarnModalOpen(false)}
                    infoType={warn}
                />
            )}
        </div>
    );
}

function LevelInfo() {
    const { levelid } = Route.useParams();
    const navigate = Route.useNavigate();

    const list = useList();

    const [isHistoryOpen, setHistoryOpen] = useState(false);
    const [groupActions, setGroupActions] = useState(false);
    const [hideOtherPlaced, setHideOtherPlaced] = useState(false);
    const [isEditLevelSheetOpen, setIsEditLevelSheetOpen] = useState(false);
    const [isMoveLevelModalOpen, setIsMoveLevelModalOpen] = useState(false);

    const baseData = Route.useLoaderData();
    const { data: idl } = useQuery(idlQuery());
    const levelIDL = idl?.find((idllevel) => idllevel.name === baseData.name);

    const {
        data: packsData,
        isLoading: packsIsLoading,
        isError: packsIsError,
    } = useQuery(levelPacksQuery({ list, levelid }));

    const { hasScope } = useScopes();

    let idlpos = -1;
    let idlid = '';

    if (baseData.status == 'Legacy') idlpos = levelIDL?.position ?? -1;
    else idlpos = levelIDL?.formerRank ?? -1;
    idlid = levelIDL?.id ?? '';

    const isNong = baseData.tags?.includes('NONG') || false;
    return (
        <div className="flex flex-col items-center w-full h-full bg-background-dark rounded-2xl clamp-[p-3-5-clamp]">
            <div className="flex flex-col items-start  w-full">
                <h1 className="flex justify-between border-b text-secondary-foreground border-secondary-light pb-2 w-full text-4xl box-border overflow-hidden text-ellipsis font-medium mb-2">
                    {baseData.name}
                    {hasScope('level_modify') && (
                        <div className="flex items-center gap-2">
                            <Pencil
                                className="h-8 w-8 hover:text-secondary-light transition-colors cursor-pointer"
                                onClick={() => setIsEditLevelSheetOpen(true)}
                            />
                            <ArrowUpDown
                                className="h-8 w-8 hover:text-secondary-light transition-colors cursor-pointer"
                                onClick={() => setIsMoveLevelModalOpen(true)}
                            />
                        </div>
                    )}
                </h1>
            </div>
            <ScrollArea className="h-[calc(100vh-220px)] w-full">
                <div className="flex flex-col items-start w-full">
                    <p className="p-0 text-start w-full clamp-[text-sm-lg-clamp]">
                        {trimStringToNull(baseData.description) ??
                            'No description was provided for this level yet.'}
                    </p>
                </div>
                <div className="w-full lg:w-11/12">
                    <div className="flex flex-col mr-4 mb-2">
                        <div className="flex flex-wrap">
                            {!packsIsError && !packsIsLoading && packsData && (
                                <LevelPacksTags
                                    items={packsData}
                                    placeholder=""
                                />
                            )}
                        </div>
                        <div className="my-3">
                            {baseData?.verifications?.length > 0 && (
                                <VideoPlayer
                                    videoUrl={
                                        baseData.verifications[
                                            baseData?.verifications.length - 1
                                        ]?.video_url
                                    }
                                />
                            )}
                        </div>

                        <div className="flex flex-wrap">
                            {baseData.tags && baseData.tags.length > 0 && (
                                <LevelGPTags
                                    items={baseData.tags.sort(sortTags)}
                                    placeholder=""
                                    onClick={(tag) => {
                                        navigate({
                                            from: '/list/$levelid',
                                            search: (prev) => ({
                                                ...prev,
                                                filters: {
                                                    tags: {
                                                        [`${tag}`]: true,
                                                    },
                                                },
                                            }),
                                        });
                                        toast.info(
                                            `Applied tag filter: ${tag}`,
                                        );
                                        return;
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex mr-4 mb-2">
                        <LevelCustomCopies />
                        {baseData.status == 'Legacy' && list == 'classic' ? (
                            <LevelDataInfo
                                label="idl rank"
                                data={idlpos != -1 ? `#${idlpos}` : '-'}
                                link={
                                    idlpos != -1
                                        ? `https://insanedemonlist.com/level/${idlid}`
                                        : undefined
                                }
                                info="IDL"
                            />
                        ) : (
                            <LevelDataInfo
                                label="list points"
                                data={String(baseData.points / 10)}
                            />
                        )}

                        <LevelDataInfo
                            label="edel enjoyment"
                            data={String(
                                baseData.edel_enjoyment
                                    ? Math.round(
                                          baseData.edel_enjoyment * 100,
                                      ) / 100
                                    : '-',
                            )}
                            info="EDEL"
                            warn={
                                baseData.is_edel_pending ? 'EDEL_PENDING' : ''
                            }
                        />
                    </div>

                    <div className="flex mr-4 mb-2">
                        <LevelDataInfo
                            label={isNong ? 'nong download' : 'song'}
                            data={
                                baseData.song
                                    ? baseData.song > 0
                                        ? String(baseData.song)
                                        : 'DEFAULT'
                                    : isNong
                                      ? 'SFH'
                                      : '-'
                            }
                            link={
                                baseData.song
                                    ? Number(baseData.song) > 0
                                        ? `https://www.newgrounds.com/audio/listen/${baseData.song}`
                                        : undefined
                                    : isNong
                                      ? `https://songfilehub.com/songs?name=${baseData.name}`
                                      : undefined
                            }
                            info={isNong ? 'SFH' : ''}
                        />
                        <LevelDataInfo
                            label="NLW Tier"
                            data={baseData.nlw_tier ?? '-'}
                            info="NLW"
                        />
                        {list == 'classic' && (
                            <LevelDataInfo
                                label="gddl tier"
                                data={String(
                                    baseData.gddl_tier
                                        ? Math.round(baseData.gddl_tier)
                                        : '-',
                                )}
                                link={
                                    baseData.gddl_tier
                                        ? `https://gdladder.com/level/${baseData.level_id}`
                                        : ''
                                }
                                info="GDDL"
                            />
                        )}
                    </div>

                    <Suspense fallback={<Load />}>
                        <LevelCreators />
                    </Suspense>

                    {hasScope('level_notes_modify') && (
                        <Suspense fallback={<Load />}>
                            <LevelNotes />
                        </Suspense>
                    )}
                    {hasScope('level_updates_modify') && (
                        <Suspense fallback={<Load />}>
                            <LevelUpdates />
                        </Suspense>
                    )}

                    <Collapsible
                        className="mt-6 mr-4 text-left"
                        open={isHistoryOpen}
                        onOpenChange={() => setHistoryOpen(!isHistoryOpen)}
                    >
                        <CollapsibleTrigger>
                            <h2 className="transition-colors text-3xl font-medium px-0.5 hover:text-secondary-light">
                                Position History
                            </h2>
                            <ChevronDown
                                className={cn(
                                    'w-6 h-6 transition-transform text-primary',
                                    isHistoryOpen && 'rotate-180',
                                )}
                            />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="flex items-center flex-wrap gap-4 my-2 lg:mx-2">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="groupActions"
                                        checked={groupActions}
                                        onCheckedChange={() =>
                                            setGroupActions(!groupActions)
                                        }
                                    />
                                    <label
                                        htmlFor="groupActions"
                                        className="text-secondary-light"
                                    >
                                        Group similar entries
                                    </label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="hideOtherPlaced"
                                        checked={hideOtherPlaced}
                                        onCheckedChange={() =>
                                            setHideOtherPlaced(!hideOtherPlaced)
                                        }
                                    />
                                    <label
                                        htmlFor="hideOtherPlaced"
                                        className="text-secondary-light"
                                    >
                                        Hide other levels placements
                                    </label>
                                </div>
                            </div>
                            <Suspense fallback={<Load />}>
                                <History
                                    groupActions={groupActions}
                                    hideOtherPlaced={hideOtherPlaced}
                                />
                            </Suspense>
                        </CollapsibleContent>
                    </Collapsible>
                </div>
            </ScrollArea>

            <EditLevelSheet
                onOpenChange={setIsEditLevelSheetOpen}
                open={isEditLevelSheetOpen}
            />
            <MoveLevelSheet
                onOpenChange={setIsMoveLevelModalOpen}
                open={isMoveLevelModalOpen}
            />
        </div>
    );
}

export default LevelInfo;
